import {
  needCombinePortModels,
  defaultCategories,
  defaultModels,
  defaultLinkSpeedDefinitions,
  defaultPortTypes,
  defaultEnableState,
  defaultRSTP,
} from "../const";

export const getInitCategory = ({
  config
}) => {
  let categories = [];

  if (config) {
    categories = structuredClone(defaultCategories).map(category => {
      category.isSelected = category.value === config.deviceCategory;
      return category;
    });
  } else {
    categories = structuredClone(defaultCategories);
  }

  return categories;
}

export const getInitModels = ({
  models,
  selectedCategory,
  selectedConfig,
}) => {
  if (!selectedCategory) return [];
  const isEditMode = !!selectedConfig;

  const tmpModels = models
    .filter(model => model.deviceCategory === selectedCategory.value)
    .map(model => {

      const truncatedHWVer = model?.hwVer ? model.hwVer.replace(/\d+$/, "") : null;
      const truncatedHWVerWithSuffix = truncatedHWVer ? truncatedHWVer + "x" : null;
      const seriesName = `${model.name}/${truncatedHWVer}`;
      const label = `${model.name} (${truncatedHWVerWithSuffix ?? "-"})`;

      return {
        ...model,
        seriesName,
        truncatedHWVer,
        truncatedHWVerWithSuffix,
        value: model.name,
        label,
        isSelected: isEditMode ? model.name === selectedConfig.formattedModelName : false,
      }
    })

  const defaultModel = {
    ...defaultModels[0],
    isSelected: !isEditMode
  };

  return [defaultModel, ...tmpModels];
}

export const getInitPortType = ({ portTypes, portIndex }) => {
  const portType = portTypes?.[portIndex - 1];

  let isCopper = false;
  let isFiber = false;

  if (portType === 1 || portType === 3) {
    // Copper
    isCopper = true;
  } else if (portType === 2 || portType === 4) {
    // Fiber
    isFiber = true;
  } else if (portType === 5 || portType === 6) {
    // Combo
    isCopper = true;
    isFiber = true;
  }

  return { isCopper, isFiber };
}

export const getFlattenLinkSpeedList = ({ linkSpeedList }) => {
  // Change namespace:
  // 1. "s" to "fiberList".
  // 2. "t" to "copperList".
  const readableList = linkSpeedList.map(linkSpeed => {
    const { count, s = [], t = [] } = linkSpeed;

    return {
      count,
      copperList: t,
      fiberList: s,
    }
  })

  // [ range1-range2, range2-range3, range3-range4, ... ].
  // e.g. [2, 3, 2] means [1-2, 3-5, 6-7].
  let rangeList = [];
  readableList.reduce((prevCount, linkSpeed) => {
    const range = prevCount + linkSpeed.count;
    rangeList.push(range);

    return range;
  }, 0);

  const totalPortCount = rangeList[rangeList.length - 1];

  // Spread the link speed by the range list.
  // e.g. [ linkSpeed-1, linkSpeed-1, linkSpeed-2, linkSpeed-2, linkSpeed-2, linkSpeed-3, ... ]
  const flattenLinkSpeedList = Array(totalPortCount).fill(null).map((_, index) => {
    const matchedIndex = rangeList.findIndex(range => index < range);
    return readableList[matchedIndex];
  })

  return {
    flattenLinkSpeedList
  }
}

export const getInitDefaultConfig = ({
  responseConfig
}) => {
  const {
    capabilities,
    "wired.port": wiredPort,
    seriesName,
  } = responseConfig;

  const {
    linkSpeed: linkSpeedList,
    portTypes
  } = capabilities;

  const [modelName, hwVer] = seriesName ? seriesName.split(/\//) : [];

  const { flattenLinkSpeedList } = getFlattenLinkSpeedList({ linkSpeedList });

  const isNeedCombineModel = needCombinePortModels.includes(modelName);
  const combinePortName = isNeedCombineModel ? wiredPort.map(port => port.portName).join("") : "";
  const combinePortIndex = isNeedCombineModel ? wiredPort.map(port => port.portIndex).join("/") : "";

  let initPortConfigs = wiredPort.map(port => {

    const {
      portIndex,
      speed,
      comboSfpSpeed,
      portType,
      aggregatePort,
      poe,
      poePowerUsage,
      poeState,
      ddpState,
      lbdState,
      rstp,
      portTags,
    } = port;

    const formattedFullName = (seriesName ?? "") + "/" + portIndex;
    const formattedPortType = defaultPortTypes[portType] ?? null;
    const isAggregate = aggregatePort?.length > 0;
    const formattedAggregate = defaultEnableState[isAggregate] ?? null;
    const formattedPoEState = defaultEnableState[poeState] ?? null;
    const formattedPoEUsageLabel = poeState !== true || (poe && parseInt(poePowerUsage, 10) < 0) ? "-" : poePowerUsage;
    const formattedRSTP = defaultRSTP[rstp] ?? null;
    const formattedDDPState = defaultEnableState[ddpState] ?? null;
    const formattedLBDState = defaultEnableState[lbdState] ?? null;
    const formattedPortTagsLabel = portTags?.length > 0 ? portTags?.join(", ") : "-";

    // speed
    const { isCopper, isFiber } = getInitPortType({ portTypes, portIndex });
    const formattedCoperSpeed = defaultLinkSpeedDefinitions[speed] ?? null;
    const formattedFiberSpeed = defaultLinkSpeedDefinitions[comboSfpSpeed] ?? null;
    const formattedFinalSpeed = isCopper ? formattedCoperSpeed : (isFiber ? formattedFiberSpeed : null);

    return {
      ...port,
      formattedFullName,
      seriesName,
      modelName,
      hwVer,
      portTypes,
      supportedLinkSpeedList: flattenLinkSpeedList[portIndex - 1],

      formattedPortType,
      formattedPortTypeLabel: formattedPortType?.label ?? "-",

      formattedAggregate,
      formattedAggregateLabel: formattedAggregate?.label ?? "-",

      formattedPoEState,
      formattedPoEStateLabel: formattedPoEState?.label ?? "-",

      formattedPoEUsageLabel,

      formattedRSTP,
      formattedRSTPLabel: formattedRSTP?.label ?? "-",

      formattedDDPState,
      formattedDDPStateLabel: formattedDDPState?.label ?? "-",

      formattedLBDState,
      formattedLBDStateLabel: formattedLBDState?.label ?? "-",

      formattedPortTagsLabel,

      // speed
      isCopper,
      isFiber,
      formattedCoperSpeed,
      formattedCoperSpeedLabel: formattedCoperSpeed?.label ?? "-",

      formattedFiberSpeed,
      formattedFiberSpeedLabel: formattedFiberSpeed?.label ?? "-",

      formattedFinalSpeed,
      formattedFinalSpeedLabel: formattedFinalSpeed?.label ?? "-",

      combinePortName,
      combinePortIndex,
      isNeedCombineModel,
      isShow: isNeedCombineModel ? portIndex === 1 : true
    }
  })
    .filter(Boolean)

  return {
    ...responseConfig,
    portConfigs: initPortConfigs
  };
}

export const getInitSelectedConfig = ({
  responseConfig,
  selectedConfig,
}) => {

  const {
    portConfigs,
  } = selectedConfig;

  const {
    capabilities,
    seriesName,
    "wired.port": wiredPort,
  } = responseConfig;

  const {
    linkSpeed: linkSpeedList,
    portTypes
  } = capabilities;

  const { flattenLinkSpeedList } = getFlattenLinkSpeedList({ linkSpeedList });

  const [modelName, hwVer] = seriesName ? seriesName.split(/\//) : [];

  const isNeedCombineModel = needCombinePortModels.includes(modelName);

  // If the current device models need to be merged and all their portNames are different, need combine the portNames together.
  const isAllNameSame = portConfigs.every(item => item.portName === portConfigs[0].portName);
  const combinePortName = isNeedCombineModel ? ( isAllNameSame ? portConfigs[0].portName : portConfigs.map(port => port.portName).join("") ) : "";
  const combinePortIndex = isNeedCombineModel ? portConfigs.map(port => port.portIndex).join("/") : "";

  const initPortConfigs = portConfigs.map(port => {

    const selectedDeviceCategory = wiredPort?.[0]?.deviceCategory;

    const {
      portIndex,
      speed,
      comboSfpSpeed,
      portType,
      aggregatePort,
      poe,
      poePowerUsage,
      poeState,
      ddpState,
      lbdState,
      rstp,
      portTags,
    } = port;

    const formattedFullName = (seriesName ?? "") + "/" + portIndex;
    const formattedPortType = defaultPortTypes[portType] ?? null;
    const isAggregate = aggregatePort?.length > 0;
    const formattedAggregate = defaultEnableState[isAggregate] ?? null;
    const formattedPoEState = defaultEnableState[poeState] ?? null;
    const formattedPoEUsageLabel = poeState !== true || (poe && parseInt(poePowerUsage, 10) < 0) ? "-" : poePowerUsage;
    const formattedRSTP = defaultRSTP[rstp] ?? null;
    const formattedDDPState = defaultEnableState[ddpState] ?? null;
    const formattedLBDState = defaultEnableState[lbdState] ?? null;
    const formattedPortTagsLabel = portTags?.length > 0 ? portTags?.join(", ") : "-";

    // speed
    const { isCopper, isFiber } = getInitPortType({ portTypes, portIndex });
    const formattedCoperSpeed = defaultLinkSpeedDefinitions[speed] ?? null;
    const formattedFiberSpeed = defaultLinkSpeedDefinitions[comboSfpSpeed] ?? null;
    const formattedFinalSpeed = isCopper ? formattedCoperSpeed : (isFiber ? formattedFiberSpeed : null);

    return {
      ...port,
      formattedFullName,
      seriesName,
      modelName,
      hwVer,
      portTypes,
      deviceCategory: selectedDeviceCategory,
      supportedLinkSpeedList: flattenLinkSpeedList[portIndex - 1],

      formattedPortType,
      formattedPortTypeLabel: formattedPortType?.label ?? "-",

      formattedAggregate,
      formattedAggregateLabel: formattedAggregate?.label ?? "-",

      formattedPoEState,
      formattedPoEStateLabel: formattedPoEState?.label ?? "-",

      formattedPoEUsageLabel,

      formattedRSTP,
      formattedRSTPLabel: formattedRSTP?.label ?? "-",

      formattedDDPState,
      formattedDDPStateLabel: formattedDDPState?.label ?? "-",

      formattedLBDState,
      formattedLBDStateLabel: formattedLBDState?.label ?? "-",

      formattedPortTagsLabel,

      // speed
      isCopper,
      isFiber,
      formattedCoperSpeed,
      formattedCoperSpeedLabel: formattedCoperSpeed?.label ?? "-",

      formattedFiberSpeed,
      formattedFiberSpeedLabel: formattedFiberSpeed?.label ?? "-",

      formattedFinalSpeed,
      formattedFinalSpeedLabel: formattedFinalSpeed?.label ?? "-",

      combinePortName,
      combinePortIndex,
      isNeedCombineModel,
      isShow: isNeedCombineModel ? portIndex === 1 : true,
    }
  })

  return {
    ...responseConfig,
    portConfigs: initPortConfigs
  };
}

export const getCleanPayload = ({
  defaultConfig,
  selectedConfig,
}) => {

  const isEditMode = !!selectedConfig;

  const {
    "wired.port": wiredPort,
    capabilities,
    ...restConfig
  } = defaultConfig;

  const {
    formattedActiveState,
    formattedModelName,
    formattedHwVer,
    formattedHwVerLabel,
    formattedPortCount,
    deviceCategory,
    ...payloadConfig
  } = restConfig;

  if (isEditMode) payloadConfig.id = selectedConfig.id;
  payloadConfig.activeState = !isEditMode ? true : selectedConfig.activeState;
  payloadConfig.portConfigs = payloadConfig.portConfigs.map(port => {

    const {
      seriesName,
      modelName,
      hwVer,
      portTypes,
      formattedPortType,
      formattedPortTypeLabel,
      formattedAggregate,
      formattedAggregateLabel,
      formattedFullName,
      formattedPoEState,
      formattedPoEStateLabel,
      formattedPoEUsageLabel,
      formattedRSTP,
      formattedRSTPLabel,
      formattedDDPState,
      formattedDDPStateLabel,
      formattedLBDState,
      formattedLBDStateLabel,
      formattedPortTagsLabel,
      isCopper,
      isFiber,
      supportedLinkSpeedList,
      formattedCoperSpeed,
      formattedCoperSpeedLabel,
      formattedFiberSpeed,
      formattedFiberSpeedLabel,
      formattedFinalSpeed,
      formattedFinalSpeedLabel,
      combinePortName,
      combinePortIndex,
      isNeedCombineModel,
      isShow,
      ...necessaryPort
    } = port;

    return necessaryPort;
  })

  const payload = {
    data: payloadConfig
  };

  return payload;
}